<script setup>
import { computed, onMounted, reactive, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useAlert } from 'dashboard/composables';
import { useLeadsStore, LEAD_STAGES } from 'dashboard/stores/leads';
import LeadCard from '../components/LeadCard.vue';

const leadsStore = useLeadsStore();
const router = useRouter();
const { t } = useI18n();

const leads = computed(() => leadsStore.getLeadsList);
const isFetchingList = computed(() => leadsStore.getUIFlags.fetchingList);

// vuedraggable mutates these arrays directly during drag, so each column
// needs its own local, writable array kept in sync with the store.
const columns = reactive(
  Object.fromEntries(LEAD_STAGES.map(stage => [stage, []]))
);

const syncColumnsFromStore = () => {
  LEAD_STAGES.forEach(stage => {
    columns[stage] = leads.value.filter(lead => lead.stage === stage);
  });
};

watch(leads, syncColumnsFromStore, { deep: false });

onMounted(async () => {
  await leadsStore.get();
  syncColumnsFromStore();
});

const openLead = lead => {
  router.push({ name: 'leads_dashboard_show', params: { leadId: lead.id } });
};

const onColumnChange = async (event, stage) => {
  const added = event?.added;
  if (!added) return;

  try {
    await leadsStore.update({ id: added.element.id, stage });
  } catch (error) {
    useAlert(t('LEADS.DETAIL.ERROR'));
    syncColumnsFromStore();
  }
};
</script>

<template>
  <div class="h-full overflow-hidden flex flex-col">
    <div class="px-4 py-3 border-b border-n-weak">
      <h1 class="text-lg font-medium text-n-slate-12">
        {{ t('LEADS.HEADER') }}
      </h1>
    </div>

    <div v-if="isFetchingList" class="flex items-center justify-center p-8">
      <span class="text-n-slate-11 text-base">{{ t('LEADS.LOADING') }}</span>
    </div>

    <div v-else class="flex-1 overflow-x-auto p-4">
      <div class="flex gap-4 h-full min-w-max">
        <div
          v-for="stage in LEAD_STAGES"
          :key="stage"
          class="w-72 flex-shrink-0 flex flex-col bg-n-solid-1 rounded-lg border border-n-weak"
        >
          <div
            class="px-3 py-2 border-b border-n-weak flex items-center justify-between"
          >
            <span class="text-sm font-medium text-n-slate-12">
              {{ t(`LEADS.STAGES.${stage}`) }}
            </span>
            <span class="text-xs text-n-slate-10">{{
              columns[stage].length
            }}</span>
          </div>
          <Draggable
            :list="columns[stage]"
            :group="{ name: 'leads-kanban', pull: true, put: true }"
            item-key="id"
            animation="200"
            ghost-class="opacity-40"
            class="flex-1 p-2 flex flex-col gap-2 min-h-[80px] overflow-y-auto"
            @change="event => onColumnChange(event, stage)"
          >
            <template #item="{ element }">
              <LeadCard :lead="element" @click="openLead(element)" />
            </template>
          </Draggable>
          <div
            v-if="!columns[stage].length"
            class="px-3 pb-3 text-xs text-n-slate-10"
          >
            {{ t('LEADS.EMPTY_STATE') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
